
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  activeClassName?: string;
  exact?: boolean;
  icon?: LucideIcon;
  badge?: number | null;
  onClick?: () => void;
  ariaLabel?: string;
}

export const NavLink = ({ 
  to, 
  children, 
  className = '',
  activeClassName = 'font-semibold text-primary',
  exact = true,
  icon: Icon,
  badge,
  onClick,
  ariaLabel
}: NavLinkProps) => {
  const location = useLocation();
  const isActive = exact 
    ? location.pathname === to 
    : location.pathname.startsWith(to);
  
  const getAriaLabel = (): string | undefined => {
    if (ariaLabel) return ariaLabel;
    if (typeof children === 'string') return children;
    return undefined;
  };
  
  return (
    <Link 
      to={to}
      className={cn(
        "transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1 rounded",
        className, 
        isActive && activeClassName
      )}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      aria-label={getAriaLabel()}
    >
      <div className="flex items-center gap-3">
        {Icon && (
          <Icon 
            className="h-4 w-4 flex-shrink-0 text-primary" 
            aria-hidden="true"
          />
        )}
        <span className="whitespace-nowrap">{children}</span>
        {badge !== undefined && badge !== null && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-accent text-accent-foreground ml-1" aria-label={`${badge} notifications`}>
            {badge}
          </span>
        )}
      </div>
    </Link>
  );
};

export default NavLink;
